import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  Link,
} from "react-router-dom";
import {
  AuthProvider,
  useAuth,
  hasAnyAccess,
  LoginPage,
  AccountSwitcher,
} from "./auth";
import { ThemeProvider, ThemeSwitcher } from "./theme";
import { I18nProvider, useI18n, LangSwitcher } from "./i18n";
import { StoreProvider, useSyncStatus, useStore, useQuery, useRpc } from "./store";
import {
  initClientDatabase,
  db,
  getDbError,
  clearDbError,
  onDbError,
  destroyDatabase,
} from "./db";
import { cases, patients, users } from "../../shared/src/schema";
import { eq } from "drizzle-orm";
import {
  useEffect,
  useState,
  useSyncExternalStore,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import { Sentry, setSentryUser } from "./sentry";
import { CasesPage } from "./pages/cases";
import { CasePage } from "./pages/case/index";
import { PatientsPage } from "./pages/patients";
import { PatientPage } from "./pages/patient";
import {
  UsersPage,
  UserPage,
  RegionsPage,
  HospitalsPage,
  UnitsPage,
  AuditPage,
} from "./pages/admin";
import { InsightsPage } from "./pages/insights";
import { MessagesPage } from "./pages/messages";
import { useStyles, useColors } from "./ui";
import { ConfirmModal } from "./confirm-modal";
import { AnonProvider, AnonSwitcher } from "./anon";
import iconCases from "./assets/icons/cases.svg";
import iconPatients from "./assets/icons/patients.svg";
import iconInsights from "./assets/icons/insights.svg";
import iconUsers from "./assets/icons/users.svg";
import iconHospitals from "./assets/icons/hospitals.svg";
import iconUnits from "./assets/icons/units.svg";
import iconAudit from "./assets/icons/audit.svg";


function AdminRoute({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  if (!user?.isAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function AdminOrMedicRole2Route({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  if (!user?.isAdmin && user?.role !== "medicRole2") return <Navigate to="/" replace />;
  return <>{children}</>;
}

function NoAccessPage() {
  const { t } = useI18n();
  const colors = useColors();
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        padding: "1.5rem",
        textAlign: "center",
        color: colors.textMuted,
        maxWidth: "440px",
        margin: "0 auto",
      }}
    >
      <p>{t("app.noAccess")}</p>
    </div>
  );
}

function AccessRoute({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  if (!hasAnyAccess(user)) return <NoAccessPage />;
  return <>{children}</>;
}

function HomePage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const { online } = useSyncStatus();
  const colors = useColors();

  if (!hasAnyAccess(user)) return <NoAccessPage />;

  const sections: Array<{
    to: string;
    icon: string;
    label: string;
    desc: string;
  }> = [
    {
      to: "/cases",
      icon: iconCases,
      label: t("nav.cases"),
      desc: t("nav.casesDesc"),
    },
    {
      to: "/patients",
      icon: iconPatients,
      label: t("nav.patients"),
      desc: t("nav.patientsDesc"),
    },
    {
      to: "/insights",
      icon: iconInsights,
      label: t("nav.insights"),
      desc: t("nav.insightsDesc"),
    },
    {
      to: "/messages",
      icon: iconAudit,
      label: t("nav.messages"),
      desc: t("nav.messagesDesc"),
    },
  ];

  const adminSections: Array<{
    to: string;
    icon: string;
    label: string;
    desc: string;
  }> = [
    {
      to: "/users",
      icon: iconUsers,
      label: t("admin.users"),
      desc: t("nav.usersDesc"),
    },
    {
      to: "/hospitals",
      icon: iconHospitals,
      label: t("admin.hospitals"),
      desc: t("nav.hospitalsDesc"),
    },
    {
      to: "/units",
      icon: iconUnits,
      label: t("admin.units"),
      desc: t("nav.unitsDesc"),
    },
    {
      to: "/audit",
      icon: iconAudit,
      label: t("admin.audit"),
      desc: t("nav.auditDesc"),
    },
  ];

  const gridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(14rem, 1fr))",
    gap: "0.5rem",
  };

  const linkStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    padding: "0.5rem",
    borderRadius: "6px",
    border: `1px solid ${colors.border}`,
    textDecoration: "none",
    color: "inherit",
  };

  const disabledStyle: React.CSSProperties = {
    ...linkStyle,
    opacity: 0.4,
    pointerEvents: "none",
  };

  const headingStyle: React.CSSProperties = {
    fontWeight: 700,
    fontSize: "0.7rem",
    color: colors.textMuted,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    margin: "0.75rem 0 0.25rem",
  };

  return (
    <div style={{ padding: "0.75rem" }}>
      <div
        style={{
          padding: "0.5rem 0.75rem",
          marginBottom: "0.75rem",
          borderRadius: "6px",
          background: colors.warning + "18",
          border: `1px solid ${colors.warning}44`,
          fontSize: "0.8rem",
          color: colors.text,
        }}
      >
        <strong style={{ color: colors.warning }}>
          {t("app.earlyPreview")}
        </strong>{" "}
        — {t("app.earlyPreviewDesc")}
      </div>
      <div style={gridStyle}>
        {sections.map((s) => (
          <Link key={s.to} to={s.to} style={linkStyle}>
            <img src={s.icon} alt="" style={{ width: "4rem", height: "4rem", flexShrink: 0 }} />
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  color: colors.text,
                }}
              >
                {s.label}
              </div>
              <div style={{ fontSize: "0.75rem", color: colors.textMuted }}>
                {s.desc}
              </div>
            </div>
          </Link>
        ))}
      </div>
      {user?.role === "medicRole2" && (
        <>
          <div style={headingStyle}>{t("nav.myUnits")}</div>
          <div style={gridStyle}>
            <Link to={online ? "/units" : "#"} style={online ? linkStyle : disabledStyle} onClick={online ? undefined : (e) => e.preventDefault()}>
              <img src={iconUnits} alt="" style={{ width: "4rem", height: "4rem", flexShrink: 0 }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: "0.85rem", color: colors.text }}>{t("admin.units")}</div>
                <div style={{ fontSize: "0.75rem", color: colors.textMuted }}>{t("nav.myUnitsDesc")}</div>
              </div>
            </Link>
          </div>
        </>
      )}
      {user?.isAdmin && (
        <>
          <div style={headingStyle}>
            {t("nav.admin")}
            {!online && (
              <span
                style={{
                  fontWeight: 400,
                  textTransform: "none",
                  letterSpacing: 0,
                }}
              >
                {" "}
                (unavailable offline)
              </span>
            )}
          </div>
          <div style={gridStyle}>
            {adminSections.map((s) => (
              <Link
                key={s.to}
                to={online ? s.to : "#"}
                style={online ? linkStyle : disabledStyle}
                onClick={online ? undefined : (e) => e.preventDefault()}
              >
                <img
                  src={s.icon}
                  alt=""
                  style={{ width: "4rem", height: "4rem", flexShrink: 0 }}
                />
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: "0.85rem",
                      color: colors.text,
                    }}
                  >
                    {s.label}
                  </div>
                  <div style={{ fontSize: "0.75rem", color: colors.textMuted }}>
                    {s.desc}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const WS_URL =
  (import.meta.env.VITE_WS_URL ||
    `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.host}`) +
  "/ws";

function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={
        <div style={{ padding: "2rem", textAlign: "center", fontFamily: "system-ui, sans-serif", colorScheme: "light dark" }}>
          <h1 style={{ fontSize: "1.25rem", marginBottom: "1rem" }}>Something went wrong</h1>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: "0.5rem 1.5rem",
              fontSize: "1rem",
              borderRadius: "6px",
              border: "1px solid ButtonBorder",
              background: "ButtonFace",
              color: "ButtonText",
              cursor: "pointer",
            }}
          >
            Reload
          </button>
        </div>
      }
    >
      <ThemeProvider>
        <I18nProvider>
          <AnonProvider>
            {isAndroidBrowser ? (
              <AndroidGate />
            ) : (
              <AuthProvider>
                <AuthGate />
              </AuthProvider>
            )}
          </AnonProvider>
        </I18nProvider>
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  );
}

function AuthErrorOverlay() {
  const { authError, clearAuthError } = useAuth();
  const { t } = useI18n();
  const colors = useColors();
  const styles = useStyles();

  if (!authError) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,0.5)",
      }}
      onClick={clearAuthError}
    >
      <div
        style={{
          background: colors.bg,
          borderRadius: "12px",
          padding: "24px",
          maxWidth: "360px",
          width: "90%",
          textAlign: "center",
          boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            background: "#dc2626",
            color: "#fff",
            padding: "12px 16px",
            borderRadius: "8px",
            marginBottom: "16px",
            fontSize: "0.9rem",
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: "4px" }}>
            {t("auth.loginError")}
          </div>
          <div style={{ fontSize: "0.8rem", opacity: 0.9 }}>
            {t("auth.notRegistered")}
          </div>
        </div>
        <button onClick={clearAuthError} style={styles.btn}>
          {t("common.dismiss")}
        </button>
      </div>
    </div>
  );
}

function DbErrorOverlay() {
  const { t } = useI18n();
  const colors = useColors();
  const styles = useStyles();
  const error = useSyncExternalStore(onDbError, getDbError);
  const [resetting, setResetting] = useState(false);

  const handleReset = useCallback(async () => {
    setResetting(true);
    try {
      await destroyDatabase();
    } catch {
      /* best effort */
    }
    window.location.reload();
  }, []);

  if (!error) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,0.5)",
      }}
      onClick={clearDbError}
    >
      <div
        style={{
          background: colors.bg,
          borderRadius: "12px",
          padding: "24px",
          maxWidth: "420px",
          width: "90%",
          textAlign: "center",
          boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            background: "#dc2626",
            color: "#fff",
            padding: "12px 16px",
            borderRadius: "8px",
            marginBottom: "16px",
            fontSize: "0.9rem",
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: "4px" }}>
            {t("db.errorTitle")}
          </div>
          <div
            style={{
              fontSize: "0.75rem",
              opacity: 0.85,
              wordBreak: "break-word",
              fontFamily: "monospace",
            }}
          >
            {error}
          </div>
        </div>
        <div
          style={{ display: "flex", gap: "0.5em", justifyContent: "center" }}
        >
          <button onClick={clearDbError} style={styles.btn}>
            {t("common.dismiss")}
          </button>
          <button
            onClick={handleReset}
            disabled={resetting}
            style={{
              ...styles.btn,
              background: "#dc2626",
              color: "#fff",
              border: "none",
            }}
          >
            {resetting ? "..." : t("db.resetApp")}
          </button>
        </div>
      </div>
    </div>
  );
}

const isAndroidBrowser =
  /Android/i.test(navigator.userAgent) && !window.DataLab;

function AndroidGate() {
  const { t } = useI18n();
  const colors = useColors();
  const styles = useStyles();
  const launchUrl =
    "datalab://" +
    window.location.host +
    window.location.pathname +
    window.location.search;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100svh",
        background: colors.bg,
        color: colors.text,
        padding: "1.5rem",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          minWidth: "240px",
          maxWidth: "360px",
          textAlign: "center",
          fontFamily: "system-ui, -apple-system, sans-serif",
        }}
      >
        <h2 style={{ margin: 0, fontWeight: 600, color: colors.text }}>
          DataLab
        </h2>
        <p style={{ margin: 0, fontSize: "0.9rem", color: colors.textMuted }}>
          {t("app.androidRequired")}
        </p>
        <a
          href={launchUrl}
          style={{
            ...styles.btnPrimary,
            textAlign: "center",
            textDecoration: "none",
          }}
        >
          {t("app.launchApp")}
        </a>
        <a
          href="/datalab.apk"
          style={{ ...styles.btn, textAlign: "center", textDecoration: "none" }}
        >
          {t("app.installButton")}
        </a>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "8px",
            marginTop: "8px",
          }}
        >
          <LangSwitcher />
          <ThemeSwitcher />
        </div>
      </div>
    </div>
  );
}

function AuthGate() {
  const { user, loading, logout } = useAuth();
  const { t } = useI18n();
  const colors = useColors();
  const [dbReady, setDbReady] = useState(false);

  useEffect(() => {
    if (user) {
      setSentryUser(user);
      // Initialize per-user database
      setDbReady(false);
      initClientDatabase(user.userId).then(() => setDbReady(true));
    }
  }, [user?.userId]);

  const loadingScreen = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        color: colors.textMuted,
      }}
    >
      {t("app.loading")}
    </div>
  );
  if (loading) return loadingScreen;
  if (!user) return <LoginPage />;
  if (!dbReady) return loadingScreen;

  return (
    <StoreProvider
      sessionId={user.sessionId}
      userId={user.userId}
      wsUrl={WS_URL}
      onAuthFailure={logout}
    >
      <BrowserRouter>
        <AuthErrorOverlay />
        <DbErrorOverlay />
        <MainLayout />
      </BrowserRouter>
    </StoreProvider>
  );
}

function MainLayout() {
  const styles = useStyles();

  return (
    <div style={styles.page}>
      <NavBar />
      <div style={styles.content}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route
            path="/insights"
            element={
              <AccessRoute>
                <InsightsPage />
              </AccessRoute>
            }
          />
          <Route
            path="/cases"
            element={
              <AccessRoute>
                <CasesPage />
              </AccessRoute>
            }
          />
          <Route
            path="/cases/:id"
            element={
              <AccessRoute>
                <CasePage />
              </AccessRoute>
            }
          />
          <Route
            path="/cases/:id/history/:eventId"
            element={
              <AccessRoute>
                <CasePage />
              </AccessRoute>
            }
          />
          <Route
            path="/patients"
            element={
              <AccessRoute>
                <PatientsPage />
              </AccessRoute>
            }
          />
          <Route
            path="/patients/:id"
            element={
              <AccessRoute>
                <PatientPage />
              </AccessRoute>
            }
          />
          <Route
            path="/users"
            element={
              <AdminRoute>
                <UsersPage />
              </AdminRoute>
            }
          />
          <Route path="/users/:id" element={<UserPage />} />
          <Route
            path="/regions"
            element={
              <AdminRoute>
                <RegionsPage />
              </AdminRoute>
            }
          />
          <Route
            path="/hospitals"
            element={
              <AdminRoute>
                <HospitalsPage />
              </AdminRoute>
            }
          />
          <Route
            path="/units"
            element={
              <AdminOrMedicRole2Route>
                <UnitsPage />
              </AdminOrMedicRole2Route>
            }
          />
          <Route
            path="/audit"
            element={
              <AdminRoute>
                <AuditPage />
              </AdminRoute>
            }
          />
          <Route
            path="/messages"
            element={
              <AccessRoute>
                <MessagesPage />
              </AccessRoute>
            }
          />
        </Routes>
      </div>
    </div>
  );
}

// ─── Breadcrumb builder ──────────────────────────────────────────────

function useBreadcrumbs(
  t: (k: string, p?: Record<string, string>) => string,
): Array<{ label: string; to?: string }> {
  const location = useLocation();
  const parts = location.pathname.split("/").filter(Boolean);
  const section = parts[0];
  const entityId = parts[1] ?? null;

  // Fetch a human-readable label for detail pages
  const { data: detailLabel } = useQuery(async () => {
    if (!entityId) return null;
    if (section === "cases") {
      const row = await db
        .select({ number: cases.number })
        .from(cases)
        .where(eq(cases.id, entityId))
        .get();
      return row?.number ? `${t("case.number")} ${row.number}` : null;
    }
    if (section === "patients") {
      const row = await db
        .select({ name: patients.name })
        .from(patients)
        .where(eq(patients.id, entityId))
        .get();
      return row?.name || null;
    }
    if (section === "users") {
      const row = await db
        .select({ name: users.name, email: users.email })
        .from(users)
        .where(eq(users.id, entityId))
        .get();
      return row?.name || row?.email || null;
    }
    return null;
  }, [section, entityId]);

  const crumbs: Array<{ label: string; to?: string }> = [];

  if (parts.length === 0) {
    crumbs.push({ label: t("app.title") });
    return crumbs;
  }

  crumbs.push({ label: t("app.title"), to: "/" });

  const labelMap: Record<string, string> = {
    insights: t("nav.insights"),
    cases: t("nav.cases"),
    patients: t("nav.patients"),
    users: t("admin.users"),
    regions: t("admin.regions"),
    hospitals: t("admin.hospitals"),
    units: t("admin.units"),
    audit: t("admin.audit"),
  };

  const sectionLabel = labelMap[section];
  if (!sectionLabel) return crumbs;

  if (parts.length === 1) {
    crumbs.push({ label: sectionLabel });
  } else {
    crumbs.push({ label: sectionLabel, to: `/${section}` });
    const id = entityId!;
    const label = detailLabel || (id.length > 8 ? id.slice(-8) : id);
    crumbs.push({ label });
  }

  return crumbs;
}

function NavBar() {
  const { t } = useI18n();
  const { online, connecting, syncing, pendingCount } = useSyncStatus();
  const styles = useStyles();
  const colors = useColors();
  const navigate = useNavigate();
  const location = useLocation();
  const { dispatch } = useStore();
  const { user } = useAuth();
  const rpc = useRpc();
  const isAdmin = user?.role === "admin";
  const [deleteModal, setDeleteModal] = useState<{ kind: "blocked"; message: string } | { kind: "confirm" } | null>(null);

  const requestDeleteCase = async () => {
    if (!caseId) return;
    try {
      const result = await rpc<{ deletable: boolean; reason?: string }>({ type: "canDelete", entityType: "case", entityId: caseId });
      if (result.deletable) {
        setDeleteModal({ kind: "confirm" });
      } else {
        setDeleteModal({ kind: "blocked", message: t("common.deleteBlocked", { reason: result.reason || "" }) });
      }
    } catch { /* ignore */ }
  };

  const confirmDeleteCase = async () => {
    if (!caseId) return;
    setDeleteModal(null);
    try {
      await rpc({ type: "deleteEntity", entityType: "case", entityId: caseId });
      navigate("/cases");
    } catch (e) {
      setDeleteModal({ kind: "blocked", message: (e as any)?.message || "Delete failed" });
    }
  };

  const breadcrumbs = useBreadcrumbs(t);

  const caseId = useMemo(() => {
    const parts = location.pathname.split("/").filter(Boolean);
    return parts[0] === "cases" && parts[1] ? parts[1] : null;
  }, [location.pathname]);
  const { data: caseArchived } = useQuery(
    () =>
      caseId
        ? db.select({ archived: cases.archived }).from(cases).where(eq(cases.id, caseId)).then((r) => r[0]?.archived ?? null)
        : Promise.resolve(null),
    [caseId],
  );

  const statusColor = online
    ? syncing
      ? colors.warning
      : colors.success
    : connecting
      ? colors.warning
      : colors.danger;
  const statusLabel = online
    ? syncing
      ? t("sync.syncing")
      : t("sync.online")
    : connecting
      ? t("sync.connecting")
      : t("sync.offline");

  return (
    <>
    <nav style={styles.nav}>
      {/* Breadcrumbs */}
      {breadcrumbs.map((crumb, i) =>
        crumb.to ? (
          <button
            key={i}
            onClick={() => navigate(crumb.to!)}
            style={{
              padding: "0.25rem 0.75rem",
              fontSize: "0.875rem",
              fontWeight: 600,
              color: colors.text,
              background: colors.bgAlt,
              border: `1px solid ${colors.border}`,
              borderRadius: "6px",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            {crumb.label}
          </button>
        ) : (
          <button
            key={i}
            disabled
            style={{
              padding: "0.25rem 0.75rem",
              fontSize: "0.875rem",
              fontWeight: 600,
              color: colors.textMuted,
              background: colors.bgAlt,
              border: `1px solid ${colors.border}`,
              borderRadius: "6px",
              cursor: "default",
              flexShrink: 0,
              opacity: 0.6,
            }}
          >
            {crumb.label}
          </button>
        ),
      )}

      <span style={{ flex: 1, minWidth: 0 }} />
      {caseId && caseArchived === 0 && (
        <button
          onClick={() => dispatch("case.archive", { uuid: caseId })}
          style={{ ...styles.btn, flexShrink: 0, color: colors.danger }}
        >
          {t("case.archive")}
        </button>
      )}
      {caseId && isAdmin && (
        <button
          onClick={requestDeleteCase}
          style={{ ...styles.btn, flexShrink: 0, color: colors.danger }}
        >
          {t("common.delete")}
        </button>
      )}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          flexShrink: 0,
          marginLeft: "auto",
        }}
      >
        <AnonSwitcher />
        <ThemeSwitcher />
        <LangSwitcher />
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.25rem",
          }}
        >
          <span
            style={{
              display: "inline-block",
              width: "0.5rem",
              height: "0.5rem",
              borderRadius: "50%",
              background: statusColor,
            }}
          />
          <span style={{ fontSize: "0.75rem", color: statusColor }}>
            {statusLabel}
          </span>
          {pendingCount > 0 && (
            <span style={{ fontSize: "0.75rem", color: colors.textMuted }}>
              ({pendingCount} {t("sync.pending")})
            </span>
          )}
        </span>
        <AccountSwitcher />
      </div>
    </nav>
    {deleteModal?.kind === "blocked" && <ConfirmModal message={deleteModal.message} onCancel={() => setDeleteModal(null)} />}
    {deleteModal?.kind === "confirm" && (
      <ConfirmModal
        message={t("common.deleteConfirm", { name: t("case.case") })}
        onCancel={() => setDeleteModal(null)}
        onConfirm={confirmDeleteCase}
      />
    )}
    </>
  );
}

export default App;
